/**
 * Canonical IncludedServices contract shared with Firestore packages.
 *
 * A missing top-level field means that service category is not included.
 * Do not store empty objects, empty arrays, false values, null, or undefined.
 * Keep the package-level `hasIncludedServices` boolean outside this model.
 */

export type ServiceSection = 'men' | 'women' | 'both';
export type ServiceQuantityUnit = 'plate' | 'carton' | 'gallon';
export type BeverageProcession = 'coffee' | 'juice';
export type DinnerType = 'buffet' | 'dinner';
export type CakeType = 'couple' | 'bride' | 'general';
export type HallDecorStyle = 'artificialFlowers';
export type WalkwayType = 'glass' | 'crystal' | 'carpet';

export interface ServiceQuantity {
  readonly quantity: number;
  readonly unit: ServiceQuantityUnit;
  readonly section?: ServiceSection;
  readonly perTable?: true;
}

export interface IncludedServiceBase {
  readonly included: true;
  readonly details?: string;
}

export interface CoffeeTeaService {
  readonly coffee?: true;
  readonly tea?: true;
  readonly details?: string;
}

export interface DinnerService {
  readonly type: DinnerType;
  readonly persons?: number;
  readonly buffetLengthMeters?: number;
  readonly section?: ServiceSection;
  readonly coupleDinnerIncluded?: true;
  readonly adjustableLength?: true;
  readonly details?: string;
}

export interface KoshahService extends IncludedServiceBase {
  readonly count?: number;
  readonly illuminated?: true;
  readonly location?: string;
}

export interface TableDecorService extends IncludedServiceBase {
  readonly tableCount?: number;
}

export interface HallDecorService extends IncludedServiceBase {
  readonly style?: HallDecorStyle;
}

export interface CakeService extends IncludedServiceBase {
  readonly type?: CakeType;
  readonly tiers?: number;
}

export interface BeveragesService {
  readonly juice?: true;
  readonly water?: true;
  readonly softDrinks?: true;
  readonly cappuccino?: true;
  readonly welcomeDrinks?: true;
  readonly drinkCount?: number;
  readonly juiceSections?: readonly ServiceSection[];
  readonly juiceQuantities?: readonly ServiceQuantity[];
  readonly waterQuantities?: readonly ServiceQuantity[];
  readonly servingProcessions?: readonly BeverageProcession[];
  readonly details?: string;
}

export interface SweetsSavoryService extends IncludedServiceBase {
  readonly sweetPlateCount?: number;
  readonly savoryPlateCount?: number;
  readonly mixedPlateCount?: number;
  readonly pastryIncluded?: true;
  readonly datesIncluded?: true;
  readonly datePlateQuantities?: readonly ServiceQuantity[];
  readonly servedTableCount?: number;
}

export interface EntertainmentService {
  readonly dj?: true;
  readonly projector?: true;
  readonly screens?: true;
  readonly screenCount?: number;
  readonly welcomeScreen?: true;
  readonly smokeMachine?: true;
  readonly laser?: true;
  readonly brideEntranceEffects?: true;
  readonly hallEquipmentIncluded?: true;
  readonly details?: string;
}

export interface CateringExtrasService {
  readonly cookingIncluded?: true;
  readonly carcassCount?: number;
  readonly details?: string;
}

export interface AmenitiesService {
  readonly tissues?: true;
  readonly soap?: true;
  readonly cologne?: true;
  readonly toothpicks?: true;
  readonly incense?: true;
  readonly charcoal?: true;
  readonly washroomSupplies?: true;
  readonly section?: ServiceSection;
  readonly details?: string;
}

export interface AccommodationService extends IncludedServiceBase {
  readonly nights?: number;
  readonly location?: string;
}

export interface WalkwayService extends IncludedServiceBase {
  readonly type?: WalkwayType;
  readonly lengthMeters?: number;
}

export interface PhotographyService extends IncludedServiceBase {
  readonly section?: ServiceSection;
}

export interface IncludedSetupItem extends IncludedServiceBase {
  readonly section?: ServiceSection;
}

export interface EventSetupService {
  readonly receptionEntrance?: IncludedSetupItem;
  readonly receptionTable?: IncludedSetupItem;
  readonly perfumeStation?: IncludedSetupItem;
  readonly makeupStation?: IncludedSetupItem;
  readonly dinnerTable?: IncludedSetupItem;
  readonly afterDinnerTable?: IncludedSetupItem;
  readonly brideAttendant?: IncludedSetupItem;
  readonly brideSpotlight?: IncludedSetupItem;
  readonly upperBenchFurnishing?: IncludedSetupItem;
  readonly courtyardFurnishing?: IncludedSetupItem;
}

export interface IncludedServiceExtra {
  readonly key: string;
  readonly label: string;
  readonly details?: string;
}

export interface IncludedServices {
  readonly hospitality?: true;
  readonly coffeeTea?: CoffeeTeaService;
  readonly dinner?: DinnerService;
  readonly koshah?: KoshahService;
  readonly tableDecor?: TableDecorService;
  readonly hallDecor?: HallDecorService;
  readonly cake?: CakeService;
  readonly beverages?: BeveragesService;
  readonly sweetsSavory?: SweetsSavoryService;
  readonly entertainment?: EntertainmentService;
  readonly cateringExtras?: CateringExtrasService;
  readonly amenities?: AmenitiesService;
  readonly accommodation?: AccommodationService;
  readonly walkway?: WalkwayService;
  readonly photography?: PhotographyService;
  readonly eventSetup?: EventSetupService;
  readonly extras?: readonly IncludedServiceExtra[];
}

/**
 * Service catalog applicable to the men's section.
 *
 * Bride-stage and women-event setup services are deliberately unavailable in
 * this type. The shared service building blocks remain identical so existing
 * Firestore maps do not need a second nested schema.
 */
export type MenIncludedServices = Omit<Pick<
  IncludedServices,
  | 'hospitality'
  | 'coffeeTea'
  | 'dinner'
  | 'beverages'
  | 'sweetsSavory'
  | 'entertainment'
  | 'cateringExtras'
  | 'amenities'
  | 'accommodation'
  | 'photography'
  | 'extras'
>, 'entertainment'> & {
  readonly entertainment?: Omit<EntertainmentService, 'brideEntranceEffects'>;
};

/** The complete catalog available to the women's section. */
export type WomenIncludedServices = IncludedServices;

export type IncludedServicesAudience = 'men' | 'women';

export const MEN_INCLUDED_SERVICE_KEYS = new Set<keyof IncludedServices>([
  'hospitality',
  'coffeeTea',
  'dinner',
  'beverages',
  'sweetsSavory',
  'entertainment',
  'cateringExtras',
  'amenities',
  'accommodation',
  'photography',
  'extras',
]);

export function assertIncludedServicesAudience(
  value: IncludedServices,
  audience: IncludedServicesAudience,
  strictSectionScope = false
): void {
  if (audience === 'men') {
    const unsupported = Object.keys(value).filter(
      (key) => !MEN_INCLUDED_SERVICE_KEYS.has(key as keyof IncludedServices)
    );
    if (unsupported.length > 0) {
      throw new Error(
        `MenIncludedServices contains women-only categories: ${unsupported.join(', ')}`
      );
    }

    const entertainment = value.entertainment;
    if (entertainment?.brideEntranceEffects === true) {
      throw new Error(
        'MenIncludedServices contains the women-only brideEntranceEffects service.'
      );
    }
  }

  if (strictSectionScope) assertSectionMarkers(value, audience);
}

function assertSectionMarkers(
  current: unknown,
  audience: IncludedServicesAudience,
  path = 'includedServices'
): void {
  if (Array.isArray(current)) {
    current.forEach((item, index) =>
      assertSectionMarkers(item, audience, `${path}[${index}]`)
    );
    return;
  }
  if (current === null || typeof current !== 'object') return;

  for (const [field, fieldValue] of Object.entries(current)) {
    if (field === 'section' && fieldValue !== audience) {
      throw new Error(`${path}.section must match ${audience}.`);
    }
    if (
      field === 'juiceSections' &&
      (!Array.isArray(fieldValue) ||
        fieldValue.some((section) => section !== audience))
    ) {
      throw new Error(`${path}.juiceSections must contain only ${audience}.`);
    }
    assertSectionMarkers(fieldValue, audience, `${path}.${field}`);
  }
}

/**
 * Use this before assigning `includedServices` to a Firestore package write.
 * The application should omit the field when the model has no service keys.
 */
export function hasIncludedServicesData(
  value: IncludedServices | null | undefined
): value is IncludedServices {
  return (
    value !== null &&
    value !== undefined &&
    Object.keys(value).length > 0
  );
}

/**
 * Validates the storage invariants before an Admin Panel Firestore write.
 */
export function assertIncludedServicesForWrite(
  value: IncludedServices,
  audience?: IncludedServicesAudience,
  strictSectionScope = false
): void {
  const visit = (current: unknown, fieldPath: string): void => {
    if (current === null || current === undefined || current === false) {
      throw new Error(`${fieldPath} contains a value that must be omitted.`);
    }

    if (Array.isArray(current)) {
      if (current.length === 0) {
        throw new Error(`${fieldPath} must not be an empty array.`);
      }

      current.forEach((item, index) =>
        visit(item, `${fieldPath}[${index}]`)
      );
      return;
    }

    if (typeof current !== 'object') return;

    const entries = Object.entries(current);

    if (entries.length === 0) {
      throw new Error(`${fieldPath} must not be an empty object.`);
    }

    entries.forEach(([fieldName, fieldValue]) =>
      visit(fieldValue, `${fieldPath}.${fieldName}`)
    );
  };

  visit(value, 'includedServices');
  if (audience) {
    assertIncludedServicesAudience(value, audience, strictSectionScope);
  }
}
